import { useEffect, useRef, useState, type ComponentType, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { CheckIcon } from '@heroicons/react/16/solid'
import { CursorArrowRaysIcon } from '@heroicons/react/24/solid'
import Board, { type BoardArrow, type BoardPiece } from '@/components/Board'
import { SideBadge } from '@/components/ui'
import { boardColors as colors } from '@/lib/boardColors'
import { boardThemeClass, useSettings } from '@/lib/settings'

/**
 * Short looping demos of every game for the website, played on the real board. They're scripted rather than driven by
 * the game rules, so the page doesn't load the chess engine.
 */

// MARK: - Timing

const TICK = 100

/**
 * Milliseconds into a demo's loop. The clock only runs while the demo is on screen; with reduced motion it stays at
 * `still`, a frame that shows the idea without moving.
 */
function useLoop(length: number, still: number): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [time, setTime] = useState(0)
  const [reduced] = useState(() => matchMedia('(prefers-reduced-motion: reduce)').matches)

  useEffect(() => {
    if (reduced || !ref.current) return
    let timer: ReturnType<typeof setInterval> | undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearInterval(timer)
        if (!entry.isIntersecting) return
        // Advance by the time that really passed: browsers slow timers down in background windows.
        let last = performance.now()
        timer = setInterval(() => {
          const now = performance.now()
          const elapsed = now - last
          last = now
          setTime((current) => (current + elapsed) % length)
        }, TICK)
      },
      { threshold: 0.4 },
    )
    observer.observe(ref.current)
    return () => {
      observer.disconnect()
      clearInterval(timer)
    }
  }, [length, reduced])

  return [ref, reduced ? still : time]
}

/** How many of `times` have come. */
function passed(time: number, times: number[]) {
  return times.filter((at) => time >= at).length
}

/** The last of `rounds` that has started, with its index. */
function currentRound<R extends { at: number }>(time: number, rounds: R[]): [R | undefined, number] {
  let index = -1
  rounds.forEach((round, position) => {
    if (time >= round.at) index = position
  })
  return [rounds[index], index]
}

// MARK: - Board helpers

const FILES = 'abcdefgh'

function piecesFromFen(fen: string): Record<string, BoardPiece> {
  const pieces: Record<string, BoardPiece> = {}
  fen.split(' ')[0].split('/').forEach((row, index) => {
    let file = 0
    for (const character of row) {
      if (/\d/.test(character)) {
        file += Number(character)
      } else {
        const color = character === character.toUpperCase() ? 'white' : 'black'
        pieces[`${FILES[file]}${8 - index}`] = { color, kind: character.toLowerCase() as BoardPiece['kind'] }
        file += 1
      }
    }
  })
  return pieces
}

function arrow(move: string, color: string): BoardArrow {
  return { from: move.slice(0, 2), to: move.slice(2, 4), color }
}

/** A square's centre, as a percentage of the board from its top-left corner (White's side). */
function squareCentre(square: string) {
  return { x: (FILES.indexOf(square[0]) + 0.5) * 12.5, y: (8 - Number(square[1]) + 0.5) * 12.5 }
}

// MARK: - Pieces of the demos

/** Board on the left and the player's side on the right, or stacked when the panel is narrow. */
function Stage({ stageRef, board, children }: { stageRef: RefObject<HTMLDivElement | null>; board?: ReactNode; children: ReactNode }) {
  return (
    <div ref={stageRef} className="flex w-full flex-col items-center justify-center gap-6 @lg:flex-row @lg:gap-10">
      {board && <div className="relative w-56 shrink-0 @lg:w-60">{board}</div>}
      <div className="flex min-w-0 flex-col items-center gap-4 @lg:w-52 @lg:items-start">{children}</div>
    </div>
  )
}

/** The move being typed, like the games' input. Green once the move is played. */
function TypedMove({ text, done }: { text: string; done: boolean }) {
  return (
    <div
      className={`flex h-12 min-w-32 items-center justify-center gap-0.5 rounded-xl bg-white px-4 text-2xl font-semibold tracking-tight shadow-sm ring-2 ring-inset dark:bg-white/10 dark:shadow-none ${done ? 'text-(--color-correct) ring-(--color-correct)' : 'ring-brand-400'}`}
    >
      {text}
      {!done && <div className="h-7 w-0.5 animate-pulse rounded-full bg-brand-400" />}
    </div>
  )
}

/** Two boxes for a square's name. */
function SquareSlots({ text, done }: { text: string; done: boolean }) {
  return (
    <div className="flex gap-2">
      {[0, 1].map((index) => (
        <div
          key={index}
          className={`grid h-14 w-12 place-items-center rounded-xl bg-white text-3xl font-semibold shadow-sm ring-inset dark:bg-white/10 dark:shadow-none ${done ? 'text-(--color-correct) ring-2 ring-(--color-correct)' : index === text.length ? 'ring-2 ring-brand-400' : 'ring-1 ring-zinc-950/10 dark:ring-white/10'}`}
        >
          {text[index] ?? ''}
        </div>
      ))}
    </div>
  )
}

/** "+15" floating up over the board after a right answer. */
function Points({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-2 flex justify-center">
      <div
        key={id}
        className="animate-[pop_900ms_ease-out_forwards] rounded-full bg-white/90 px-3 py-1 text-base font-semibold text-(--color-correct) tabular-nums opacity-0 shadow-md ring-1 ring-black/5 dark:bg-zinc-900/90 dark:shadow-none dark:ring-white/10"
      >
        {children}
      </div>
    </div>
  )
}

function Score({ value }: { value: number }) {
  return (
    <div className="text-base/7 text-zinc-500 tabular-nums sm:text-sm/6 dark:text-zinc-400">
      Score <strong className="font-semibold text-zinc-950 dark:text-white">{value}</strong>
    </div>
  )
}

function Caption({ tone = 'muted', children }: { tone?: 'muted' | 'right' | 'hint'; children: ReactNode }) {
  const color = { muted: 'text-zinc-500 dark:text-zinc-400', right: 'font-semibold text-(--color-correct)', hint: 'font-medium text-sky-600 dark:text-sky-400' }[tone]
  return <p className={`min-h-7 text-center text-base/7 text-pretty sm:text-sm/6 @lg:text-left ${color}`}>{children}</p>
}

// MARK: - The demos

const SCHOLARS_MATE = 'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w'
const SCHOLARS_MATE_DONE = 'r1bqkb1r/pppp1Qpp/2n2n2/4p3/2B1P3/8/PPPP1PPP/RNB1K1NR b'
const QXF7_PARTS = [['Q', 'queen'], ['x', 'takes'], ['f7', 'on f7'], ['#', 'checkmate']]

/** Qxf7# typed and played, then taken apart symbol by symbol. */
function LearnNotationDemo() {
  const [ref, time] = useLoop(9000, 8000)
  const typed = 'Qxf7#'.slice(0, passed(time, [700, 1000, 1300, 1600, 1900]))
  const played = time >= 2100
  const parts = passed(time, [2500, 2800, 3100, 3400])

  const highlights: Record<string, string> = {}
  if (played) {
    highlights.h5 = colors.lastMove
    highlights.f7 = colors.lastMove
  } else {
    if (typed.length >= 1) highlights.h5 = colors.typed
    if (typed.length >= 4) highlights.f7 = colors.typed
  }

  return (
    <Stage
      stageRef={ref}
      board={
        <Board
          pieces={piecesFromFen(played ? SCHOLARS_MATE_DONE : SCHOLARS_MATE)}
          highlights={highlights}
          rings={played ? { e8: colors.wrong } : {}}
          arrows={played ? [arrow('h5f7', colors.right)] : []}
        />
      }
    >
      <TypedMove text={typed} done={played} />
      <ol role="list" className="grid grid-cols-4 gap-1.5 @lg:grid-cols-2">
        {QXF7_PARTS.map(([symbol, meaning], index) => (
          <li
            key={symbol}
            className={`flex min-w-12 flex-col items-center rounded-lg bg-white px-2 py-1.5 shadow-sm ring-1 ring-zinc-950/5 transition duration-300 dark:bg-white/10 dark:shadow-none dark:ring-0 ${index < parts ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`}
          >
            <div className="text-lg font-semibold tracking-tight">{symbol}</div>
            <div className="text-sm/5 whitespace-nowrap text-zinc-500 dark:text-zinc-400">{meaning}</div>
          </li>
        ))}
      </ol>
    </Stage>
  )
}

const NAME_ROUNDS = [{ square: 'd5', at: 0 }, { square: 'g2', at: 2800 }, { square: 'b7', at: 5600 }]

/** Squares light up one after another and get named. */
function NameTheSquareDemo() {
  const [ref, time] = useLoop(8800, 1800)
  const [round, index] = currentRound(time, NAME_ROUNDS)
  const typed = round ? round.square.slice(0, passed(time, [round.at + 800, round.at + 1150])) : ''
  const done = typed.length === 2
  const score = 15 * NAME_ROUNDS.filter((r) => time >= r.at + 1150).length

  return (
    <Stage
      stageRef={ref}
      board={
        <>
          <Board highlights={round ? { [round.square]: done ? colors.right : 'var(--color-target)' } : {}} />
          {done && <Points id={`name-${index}`}>{`${round?.square} +15`}</Points>}
        </>
      }
    >
      <SquareSlots text={typed} done={done} />
      <Score value={score} />
      <Caption>Type the lit square's name: the letter, then the number.</Caption>
    </Stage>
  )
}

const FIND_ROUNDS = [{ square: 'g6', at: 0 }, { square: 'c3', at: 2900 }, { square: 'e7', at: 5800 }]

/** A square is named; the pointer finds it and clicks. */
function FindTheSquareDemo() {
  const [ref, time] = useLoop(9000, 2000)
  const [round, index] = currentRound(time, FIND_ROUNDS)
  const moving = round && time >= round.at + 400
  const clicked = round && time >= round.at + 1500
  const target = moving ? round.square : (FIND_ROUNDS[index - 1]?.square ?? 'e1')
  const { x, y } = squareCentre(target)
  const pressing = round && time >= round.at + 1500 && time < round.at + 1700
  const score = 15 * FIND_ROUNDS.filter((r) => time >= r.at + 1500).length

  return (
    <Stage
      stageRef={ref}
      board={
        <>
          <Board highlights={clicked ? { [round.square]: colors.right } : {}} />
          <div
            className="pointer-events-none absolute top-(--y) left-(--x) transition-[left,top] duration-700 ease-in-out"
            style={{ '--x': `${x}%`, '--y': `${y + (moving ? 0 : 60)}%` } as CSSProperties}
          >
            <CursorArrowRaysIcon
              className={`size-6 shrink-0 -translate-x-[45%] -translate-y-[38%] fill-zinc-950 drop-shadow-[0_0_1.5px_white] transition-transform duration-150 ${pressing ? 'scale-75' : ''}`}
              aria-hidden="true"
            />
          </div>
          {clicked && <Points id={`find-${index}`}>+15</Points>}
        </>
      }
    >
      <div className="text-4xl font-semibold tracking-tight">
        Find <span className="text-brand-600 dark:text-brand-400">{round?.square}</span>
      </div>
      <Score value={score} />
      <Caption>Click it. The board has no labels to help.</Caption>
    </Stage>
  )
}

const SHADE_ROUNDS = [
  { square: 'g5', dark: true, at: 0 },
  { square: 'b3', dark: false, at: 2900 },
  { square: 'h8', dark: true, at: 5800 },
]

/** A square is named; the right shade gets picked, no board needed. */
function LightOrDarkDemo() {
  const [ref, time] = useLoop(9000, 2000)
  const { boardTheme } = useSettings()
  const [round, index] = currentRound(time, SHADE_ROUNDS)
  const answered = round && time >= round.at + 1300
  const score = 15 * SHADE_ROUNDS.filter((r) => time >= r.at + 1300).length

  const option = (dark: boolean) => {
    const chosen = answered && round.dark === dark
    return (
      <div
        className={`flex items-center gap-x-2 rounded-xl bg-white py-2.5 pr-4 pl-3 text-lg font-semibold shadow-sm transition duration-200 dark:bg-white/10 dark:shadow-none ${chosen ? 'scale-105 ring-2 ring-(--color-correct)' : 'ring-1 ring-zinc-950/10 dark:ring-white/10'}`}
      >
        <div className={`size-5 shrink-0 rounded ring-1 ring-black/10 ${dark ? 'bg-(--board-dark)' : 'bg-(--board-light)'}`} />
        {dark ? 'Dark' : 'Light'}
        {chosen && <CheckIcon className="size-4 shrink-0 fill-(--color-correct)" aria-hidden="true" />}
      </div>
    )
  }

  return (
    <div ref={ref} className={`${boardThemeClass(boardTheme)} relative flex w-full flex-col items-center gap-6`}>
      <div key={round?.square ?? 'none'} className="animate-[countdown_300ms_ease-out] text-7xl font-semibold tracking-tight">
        {round?.square ?? ''}
      </div>
      <div className="flex gap-3">
        {option(false)}
        {option(true)}
      </div>
      <Score value={score} />
      {answered && <Points id={`shade-${index}`}>+15</Points>}
    </div>
  )
}

const BACK_RANK = '6k1/5ppp/8/8/8/8/5PPP/R5K1 w'
const BACK_RANK_DONE = 'R5k1/5ppp/8/8/8/8/5PPP/6K1 b'

/** The mate is typed letter by letter, the board following along, then played. */
function MateInOneDemo() {
  const [ref, time] = useLoop(8500, 7000)
  const typed = 'Ra8#'.slice(0, passed(time, [900, 1200, 1500, 1800]))
  const played = time >= 1900

  const highlights: Record<string, string> = {}
  if (played) {
    highlights.a1 = colors.lastMove
    highlights.a8 = colors.lastMove
  } else {
    if (typed.length >= 1) highlights.a1 = colors.typed
    if (typed.length >= 3) highlights.a8 = colors.typed
  }

  return (
    <Stage
      stageRef={ref}
      board={
        <>
          <Board
            pieces={piecesFromFen(played ? BACK_RANK_DONE : BACK_RANK)}
            highlights={highlights}
            rings={played ? { g8: colors.wrong } : {}}
            arrows={played ? [arrow('a1a8', colors.right)] : []}
          />
          {played && <Points id="mate">Ra8# +45</Points>}
        </>
      }
    >
      <SideBadge side="white">White to move</SideBadge>
      <TypedMove text={typed} done={played} />
      {played ? <Caption tone="right">Checkmate! A back-rank mate.</Caption> : <Caption>Find the mate and write it down.</Caption>}
    </Stage>
  )
}

const PONZIANI = 'r1bqkb1r/pppp1ppp/2n2n2/4p3/4P3/2P2N2/PP1P1PPP/RNBQKB1R w'
/** The Ponziani's main line from move 4. The player's moves come with a learning arrow, then get typed. */
const PONZIANI_MOVES = [
  { san: 'd4', move: 'd2d4', fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/3PP3/2P2N2/PP3PPP/RNBQKB1R b', arrowAt: 0, keys: [1100, 1400] },
  { san: 'Nxe4', move: 'f6e4', fen: 'r1bqkb1r/pppp1ppp/2n5/4p3/3Pn3/2P2N2/PP3PPP/RNBQKB1R w', at: 2300 },
  { san: 'd5', move: 'd4d5', fen: 'r1bqkb1r/pppp1ppp/2n5/3Pp3/4n3/2P2N2/PP3PPP/RNBQKB1R b', arrowAt: 3000, keys: [3800, 4100] },
  { san: 'Ne7', move: 'c6e7', fen: 'r1bqkb1r/ppppnppp/8/3Pp3/4n3/2P2N2/PP3PPP/RNBQKB1R w', at: 5000 },
  { san: 'Nxe5', move: 'f3e5', fen: 'r1bqkb1r/ppppnppp/8/3PN3/4n3/2P5/PP3PPP/RNBQKB1R b', arrowAt: 5700, keys: [6500, 6800, 7100, 7400] },
]

/** A new line: follow the arrows, type each move, and the game answers. */
function OpeningsDemo() {
  const [ref, time] = useLoop(10500, 7800)
  const playedAt = (move: (typeof PONZIANI_MOVES)[number]) => (move.keys ? move.keys[move.keys.length - 1] : (move.at ?? 0))
  const played = PONZIANI_MOVES.filter((move) => time >= playedAt(move))
  const last = played[played.length - 1]
  const next = PONZIANI_MOVES.find((move) => !played.includes(move))
  const learning = next?.keys && time >= next.arrowAt ? next : undefined
  const justPlayed = last?.keys && time < playedAt(last) + 900

  const highlights: Record<string, string> = {}
  let arrows: BoardArrow[] = []
  if (justPlayed && last) {
    arrows = [arrow(last.move, colors.right)]
  } else {
    if (last) {
      highlights[last.move.slice(0, 2)] = colors.lastMove
      highlights[last.move.slice(2, 4)] = colors.lastMove
    }
    if (learning) arrows = [arrow(learning.move, colors.hint)]
  }
  const typed = learning ? learning.san.slice(0, passed(time, learning.keys ?? [])) : (justPlayed ? last?.san ?? '' : '')
  const moveList = ['3. c3 Nf6', ...played.map((move, index) => (index % 2 === 0 ? `${4 + index / 2}. ${move.san}` : move.san))].join(' ')

  return (
    <Stage stageRef={ref} board={<Board pieces={piecesFromFen(last?.fen ?? PONZIANI)} highlights={highlights} arrows={arrows} />}>
      <div className="text-base/7 font-semibold sm:text-sm/6">Ponziani Opening</div>
      <p className="max-w-56 text-center font-mono text-base/7 text-zinc-500 sm:text-sm/6 @lg:text-left dark:text-zinc-400">… {moveList}</p>
      <TypedMove text={typed} done={!!justPlayed} />
      {justPlayed ? <Caption tone="right">Book move</Caption> : learning ? <Caption tone="hint">New line: write the blue arrow's move.</Caption> : <Caption>The game answers…</Caption>}
    </Stage>
  )
}

/** Demos by the name the website's panels ask for in `data-demo`. */
export const demos: Record<string, ComponentType> = {
  'learn-notation': LearnNotationDemo,
  'name-the-square': NameTheSquareDemo,
  'find-the-square': FindTheSquareDemo,
  'light-or-dark': LightOrDarkDemo,
  'mate-in-one': MateInOneDemo,
  openings: OpeningsDemo,
}
